import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import api from '../api/client';
import { getNotificationSocket } from '../api/socket';
import './Notifications.css';

const PAGE = 25;

function timeAgo(date) {
    if (!date) return '';
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
}

function buildText(n, t) {
    const first = n.actors?.[0]?.username || 'Someone';
    const others = n.count > 1 ? ` ${t('notif-and', 'and')} ${n.count - 1} ${t('notif-others', 'others')}` : '';
    const who = `${first}${others}`;
    switch (n.type) {
        case 'FOLLOW':
            return `${who} ${t('notif-followed', 'started following you')}`;
        case 'PICTURE_LIKE':
            return `${who} ${t('notif-liked-picture', 'liked your artwork')}`;
        case 'PICTURE_COMMENT':
            return `${who} ${t('notif-commented', 'commented on your artwork')}`;
        case 'COMMENT_LIKE':
            return `${who} ${t('notif-liked-comment', 'liked your comment')}`;
        case 'COMMENT_REPLY':
            return `${who} ${t('notif-replied', 'replied to your comment')}`;
        default:
            return who;
    }
}

export default function Notifications({ translations }) {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const t = (key, fallback) => translations?.[key] || fallback;

    const load = useCallback(async (from) => {
        const res = await api.getNotifications(from, PAGE);
        const list = res?.items || [];
        setItems((prev) => (from === 0 ? list : [...prev, ...list]));
        setSkip(from + list.length);
        setHasMore(from + list.length < (res?.total || 0));
    }, []);

    useEffect(() => {
        let active = true;
        api.markAllNotificationsRead().catch(() => {});
        load(0).catch(() => {}).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [load]);

    useEffect(() => {
        const socket = getNotificationSocket();
        if (!socket) return undefined;
        const onNew = () => { load(0).catch(() => {}); };
        socket.on('notification:new', onNew);
        return () => { socket.off('notification:new', onNew); };
    }, [load]);

    const handleClick = (n) => {
        if (n.picture?.publicId) {
            navigate(`/picture/${n.picture.publicId}`);
        } else if (n.type === 'FOLLOW' && n.actors?.[0]?.publicId) {
            navigate(`/user/${n.actors[0].publicId}`);
        }
    };

    return (
        <>
            <Header translations={translations} />
            <div className="notif-container">
                <h1 className="notif-title">{t('notif-title', 'Notifications')}</h1>
                {loading ? (
                    <div className="loading-page"><div className="spinner" /></div>
                ) : items.length === 0 ? (
                    <p className="notif-empty">{t('notif-empty', 'No notifications yet.')}</p>
                ) : (
                    <ul className="notif-list">
                        {items.map((n) => {
                            const actor = n.actors?.[0];
                            return (
                                <li
                                    key={n.id}
                                    className={`notif-item ${n.isRead ? '' : 'unread'}`}
                                    onClick={() => handleClick(n)}
                                >
                                    <Link
                                        to={actor ? `/user/${actor.publicId}` : '#'}
                                        className="notif-avatar-link"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {actor?.profilePicture ? (
                                            <img src={actor.profilePicture} alt="" className="notif-avatar" />
                                        ) : (
                                            <span className="notif-avatar notif-avatar-fallback">
                                                {(actor?.username || '?')[0].toUpperCase()}
                                            </span>
                                        )}
                                    </Link>
                                    <div className="notif-body">
                                        <span className="notif-text">{buildText(n, t)}</span>
                                        <span className="notif-time">{timeAgo(n.createdAt)}</span>
                                    </div>
                                    {n.picture?.thumbnailUrl && (
                                        <img src={n.picture.thumbnailUrl} alt="" className="notif-thumb" />
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
                {hasMore && (
                    <button className="notif-more" onClick={() => load(skip)}>
                        {t('notif-load-more', 'Load more')}
                    </button>
                )}
            </div>
        </>
    );
}
